import { CHALLENGE_HEARTBEAT_MAX, LOCALE, LOCALE_LANG_CODES } from '../constants';
import TBL_LANGUAGE from '../constants/tblLanguage.js';
import i18n from '../context/language/i18n';

/** challenge_name_* 키의 각 언어 문구 → 전체 i18n 키 (포인트 로그 message에 저장된 displayName 역매핑) */
const buildChallengeDisplayTextToNameKey = () => {
    const map = new Map();
    for (const row of TBL_LANGUAGE) {
        if (!row?.key || !String(row.key).startsWith('challenge_name_')) continue;
        for (const lang of LOCALE_LANG_CODES) {
            const v = row[lang];
            if (v == null || typeof v !== 'string') continue;
            const trimmed = v.trim();
            if (!trimmed) continue;
            map.set(trimmed, row.key);
            const compact = trimmed.replace(/\s+/g, '');
            if (compact) map.set(compact, row.key);
        }
    }
    return map;
};

const CHALLENGE_DISPLAY_TEXT_TO_NAME_KEY = buildChallengeDisplayTextToNameKey();

const resolveChallengeNameI18nKeyFromDisplayText = displayText => {
    if (!displayText) return null;
    const trimmed = String(displayText).trim();
    if (!trimmed) return null;
    return (
        CHALLENGE_DISPLAY_TEXT_TO_NAME_KEY.get(trimmed) ||
        CHALLENGE_DISPLAY_TEXT_TO_NAME_KEY.get(trimmed.replace(/\s+/g, '')) ||
        null
    );
};

/** 포인트 로그 객체에서 챌린지 식별자(physical_heartbeat_daily 등) 추출 — 한글 displayName과 구분 */
const pickChallengeNameKeyFromPointLog = item => {
    if (!item || typeof item !== 'object') return undefined;
    if (item.challengeNameKey) return item.challengeNameKey;
    if (item.challenge?.name) return item.challenge.name;
    if (typeof item.challengeName === 'string' && /^[a-z][a-z0-9_]*$/i.test(item.challengeName)) {
        return item.challengeName;
    }
    return undefined;
};

// 생년월일로 나이계산
export const getBirthToAge = birth => {
    if (!birth) return null; // 혹은 오류 처리, 20 반환은 임의적일 수 있음

    // 입력 형식 클리닝 강화 (숫자만 남기기)
    let yyyymmdd = birth.replace(/[^0-9]/g, '');
    if (yyyymmdd.length !== 8) {
        console.error('Invalid birth date format:', birth);
        return null; // 잘못된 형식이면 null 반환
    }

    var year = yyyymmdd.substring(0, 4);
    var month = yyyymmdd.substring(4, 6);
    var day = yyyymmdd.substring(6, 8);

    // Date 객체 생성 및 유효성 검사
    let birthDay = new Date(year, month - 1, day);
    // 생성된 날짜가 입력과 다른 경우 (e.g., new Date(2023, 1, 30) -> 3월 2일) 유효하지 않음 처리
    if (
        birthDay.getFullYear() !== parseInt(year) ||
        birthDay.getMonth() !== parseInt(month) - 1 ||
        birthDay.getDate() !== parseInt(day)
    ) {
        console.error('Invalid date created:', birth);
        return null;
    }

    let now = new Date();

    let age = now.getFullYear() - birthDay.getFullYear();

    // 만약 age가 0 미만이면 (미래 날짜 입력 등) 오류 처리
    if (age < 0) {
        console.error('Birth date is in the future:', birth);
        return null;
    }

    // 올해 태어난 경우 (age === 0)는 무조건 0살 반환
    if (age === 0) {
        return 0;
    }

    // 기존 만 나이 계산 (생일 안 지났으면 -1)
    let todayMonth = now.getMonth() + 1;
    let birthMonth = birthDay.getMonth() + 1;

    // 생일이 지나지 않았으면 age - 1 반환 (생일 당일 포함하지 않음)
    if (
        birthMonth > todayMonth ||
        (birthMonth === todayMonth && birthDay.getDate() > now.getDate())
    ) {
        return age - 1;
    } else {
        return age; // 생일이 지났거나 오늘이 생일이면 age 반환
    }
};

// 생년월일로 세대
export const getBirthToGenerationString = (birth) => {
    if (!birth) return 0;

    let yyyymmdd = birth.replace('-', '');
    var year = yyyymmdd.substring(0, 4);
    var month = yyyymmdd.substring(4, 6);
    var day = yyyymmdd.substring(6, 8);

    let birthDay = new Date(year, month - 1, day);
    let now = new Date();

    let ageDiff = now.getFullYear() - birthDay.getFullYear();
    let monthDiff = now.getMonth() - birthDay.getMonth();
    if (
        monthDiff < 0 ||
        (monthDiff === 0 && now.getDate() < birthDay.getDate())
    )
        ageDiff--;

    let decade = Math.floor(ageDiff / 10) * 10;
    return decade === 0
        ? '10대 이하'
        : `${decade}대`;
};

// 성별
export const getGenderString = (gender) => {
    gender = parseInt(gender);
    return gender === 0
        ? '남성'
        : gender === 1
            ? '여성'
            : '알수없음';
};

// 두 날짜 차이 일수
export const getDiffDate = (date1, date2 = null) => {
    let d1 = new Date(date1);
    let d2 = date2 ? new Date(date2) : new Date();
    let diffDate = d1.getTime() - d2.getTime();
    return Math.floor(Math.abs(diffDate / (1000 * 60 * 60 * 24))); // 밀리세컨 * 초 * 분 * 시 = 일
};

// 날짜 파싱
export const parseDate = dateStr => {
    const [month, day] = dateStr.split('/');
    const year = new Date().getFullYear();
    return new Date(year, parseInt(month) - 1, parseInt(day));
};

/**
 * 로케일별 날짜 입력 문자열을 yyyymmdd(8자리)로 파싱.
 * - ko, ja: YYYY.MM.DD / YYYY/MM/DD (년.월.일)
 * - en: MM/DD/YYYY (월/일/년)
 * - de: DD.MM.YYYY (일.월.년)
 * @param {string} inputStr - 사용자 입력 (예: "3/15/1990", "1990. 3. 15.", "15.3.1990")
 * @param {string} langCode - 'ko' | 'en' | 'ja' | 'de'
 * @returns {string|null} - '19900315' 또는 null
 */
export const parseLocaleDateToYyyymmdd = (inputStr, langCode) => {
    if (!inputStr || typeof inputStr !== 'string') return null;
    const trimmed = inputStr.trim();
    const digitsOnly = trimmed.replace(/[^\d]/g, '');
    if (digitsOnly.length === 8) {
        let year, month, day;
        if (langCode === 'ko' || langCode === 'ja') {
            year = parseInt(digitsOnly.slice(0, 4), 10);
            month = parseInt(digitsOnly.slice(4, 6), 10);
            day = parseInt(digitsOnly.slice(6, 8), 10);
        } else if (langCode === 'en') {
            month = parseInt(digitsOnly.slice(0, 2), 10);
            day = parseInt(digitsOnly.slice(2, 4), 10);
            year = parseInt(digitsOnly.slice(4, 8), 10);
        } else {
            day = parseInt(digitsOnly.slice(0, 2), 10);
            month = parseInt(digitsOnly.slice(2, 4), 10);
            year = parseInt(digitsOnly.slice(4, 8), 10);
        }
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
            const date = new Date(year, month - 1, day);
            if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
                return `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;
            }
        }
    }
    const rawParts = trimmed.split(/[^\d]+/).filter(Boolean);
    const parts = rawParts.map(p => parseInt(p, 10));
    if (parts.length !== 3) return null;
    const expandYear = (n) => (n < 100 ? (n < 50 ? n + 2000 : n + 1900) : n);
    let year, month, day;
    const yearLike = parts.findIndex((p) => p >= 1900 && p <= 2100);
    if (yearLike >= 0) {
        year = parts[yearLike];
        const [a, b] = parts.filter((_, i) => i !== yearLike);
        if (langCode === 'ko' || langCode === 'ja') {
            month = a;
            day = b;
        } else if (langCode === 'en') {
            month = a;
            day = b;
        } else {
            day = a;
            month = b;
        }
    } else {
        if (langCode === 'ko' || langCode === 'ja') {
            year = expandYear(parts[0]);
            month = parts[1];
            day = parts[2];
        } else if (langCode === 'en') {
            month = parts[0];
            day = parts[1];
            year = expandYear(parts[2]);
        } else {
            day = parts[0];
            month = parts[1];
            year = expandYear(parts[2]);
        }
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
    return `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;
};

/**
 * 화면 표시용 날짜/시간
 * - 시간대: 실행 환경 기본(브라우저에서는 사용자 로컬, toLocaleDateString()과 동일). timeZone 미지정.
 * - 표기법: language.js에서 설정한 앱 언어(i18n.language)에 해당하는 locale로 표기(ko-KR, en-US, ja-JP, de-DE).
 * @param {string|Date} dateString
 * @param {'date'|'time'|'datetime'|'monthDay'|'yearMonth'|'year'|'month'} formatType - date: 날짜만, time: 시간만, datetime: 둘 다, monthDay: 월/일만(연도 없음), yearMonth: 연도·월(언어별 표기), year: 연도만, month: 월만(언어별 표기, 예: 11월/November)
 */
export const formatDateTimeDisplay = (dateString, formatType = 'datetime') => {
    if (!dateString) return '';
    let date;
    if (typeof dateString === 'string') {
        const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateString).trim());
        if (dateOnly) {
            const y = parseInt(dateOnly[1], 10);
            const mo = parseInt(dateOnly[2], 10);
            const d = parseInt(dateOnly[3], 10);
            date = new Date(y, mo - 1, d);
        } else {
            date = new Date(dateString);
        }
    } else {
        date = new Date(dateString);
    }
    if (Number.isNaN(date.getTime())) return '';
    const locale = LOCALE.find(l => l.code === i18n.language)?.locale;
    const optsMap = {
        date: { year: 'numeric', month: '2-digit', day: '2-digit' },
        time: { hour: 'numeric', minute: '2-digit' },
        datetime: { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' },
        monthDay: { month: 'numeric', day: 'numeric' },
        yearMonth: { year: 'numeric', month: 'long' },
        year: { year: 'numeric' },
        month: { month: 'long' },
    };
    const opts = optsMap[formatType];
    if (!opts) return new Intl.DateTimeFormat(locale, optsMap.datetime).format(date);
    return new Intl.DateTimeFormat(locale, opts).format(date);
};

/**
 * 차트 등에서 사용하는 짧은 월 표기 (month_label 키 사용)
 * - ko: 11월, ja: 11月, de: 11, en: 11
 * @param {string} yearMonthStr - "2024-11" 형식
 */
export const formatMonthDisplay = (yearMonthStr) => {
    if (!yearMonthStr) return '';
    const parts = String(yearMonthStr).split('-');
    const month = parts.length >= 2 ? parseInt(parts[1], 10) : 0;
    if (month < 1 || month > 12) return yearMonthStr;
    return i18n.t('month_label', { month });
};

/**
 * 포인트 로그 챌린지 메시지를 다국어 표시 문자열로 변환
 * 형식: {(M/D + 공백) or ''} + challenge displayName + (' 구간 목표 도달' or ' 목표 달성')
 * @param {object} [pointLog] - 선택. challengeNameKey / challenge.name / challengeName(영문 스네이크) 등이 있으면 challenge_name_<name> 우선
 */
export const formatChallengePointMessage = (message, t, i18nInstance = i18n, pointLog) => {
    if (!message) return '';

    const raw = String(message).trim();
    if (!raw) return '';

    let rest = raw;
    let datePart = '';
    let suffixPart = '';

    // 앞부분 날짜(M/D + 공백 1칸) 파싱 및 로케일 포맷
    const dateMatch = rest.match(/^(\d{1,2})\/(\d{1,2})\s/);
    if (dateMatch) {
        const month = Number(dateMatch[1]);
        const day = Number(dateMatch[2]);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            const year = new Date().getFullYear();
            datePart = formatDateTimeDisplay(
                `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
                'monthDay'
            );
        } else {
            datePart = `${dateMatch[1]}/${dateMatch[2]}`;
        }
        rest = rest.slice(dateMatch[0].length);
    }

    // 뒷부분 상태 문구 파싱 및 번역
    if (/\s구간 목표 도달$/.test(rest)) {
        rest = rest.replace(/\s구간 목표 도달$/, '').trim();
        suffixPart = t ? t('zone_goal_reached') : '구간 목표 도달';
    } else if (/\s목표 달성$/.test(rest)) {
        rest = rest.replace(/\s목표 달성$/, '').trim();
        suffixPart = t ? t('goal_achieved') : '목표 달성';
    }

    const displayNameFromMessage = rest.trim();
    let challengeTitle = displayNameFromMessage;

    if (challengeTitle && t) {
        const apiNameKey = pickChallengeNameKeyFromPointLog(pointLog);
        const i18nKeyFromApi =
            apiNameKey && i18nInstance?.exists?.(`challenge_name_${apiNameKey}`)
                ? `challenge_name_${apiNameKey}`
                : null;
        const i18nKeyFromDisplay = resolveChallengeNameI18nKeyFromDisplayText(displayNameFromMessage);
        const i18nKeyLiteral =
            i18nKeyFromApi ||
            i18nKeyFromDisplay ||
            (i18nInstance?.exists?.(`challenge_name_${displayNameFromMessage}`)
                ? `challenge_name_${displayNameFromMessage}`
                : null);

        if (i18nKeyLiteral) {
            challengeTitle = t(i18nKeyLiteral);
        }
    }

    return [datePart, challengeTitle, suffixPart].filter(Boolean).join(' ');
};

// 날짜 포맷팅 함수 (내부 비교/API용 고정 포맷; 표시용은 formatDateTimeDisplay 사용)
export const formatDateTime = (
    dateString,
    dateFormat = '.',
    timeFormat = ':',
    startIndex = 0,
    sliceIndex = 19,
    is12Hour = false
) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = is12Hour ? String(date.getHours() % 12 || 12).padStart(2, '0') : String(date.getHours()).padStart(2, '0');
    const ampm = is12Hour ? (date.getHours() < 12 ? i18n.t('am') : i18n.t('pm')) : '';
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${dateFormat}${month}${dateFormat}${day}${' '}${ampm}${hours}${timeFormat}${minutes}${timeFormat}${seconds}`.slice(
        startIndex,
        sliceIndex
    );
};

// Date 객체를 MySQL 형식 문자열로 변환 (YYYY-MM-DD HH:mm:ss)
export const formatDateForMySQL = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/** 날짜/시간 문자열을 date input 값으로 변환 (YYYY-MM-DD) */
export const formatDateTimeForInput = (dateTimeString) => {
    if (!dateTimeString) return '';
    const d = new Date(dateTimeString);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * date input의 "YYYY-MM-DD" 문자열을 MySQL 형식으로 변환 (게시일 00:00:00, 마감일 23:59:59).
 * 타임존과 무관하게 동일한 날짜 선택 → 동일한 "YYYY-MM-DD HH:mm:ss" 문자열이 됨.
 */
export const formatDateForEventMySQL = (dateString, isEndDate = false) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const datePart = `${year}-${month}-${day}`;
    return isEndDate ? `${datePart} 23:59:59` : `${datePart} 00:00:00`;
};

export const calculateTimeDifference = (datetime) => {
    if (!datetime) return null;

    const now = new Date();
    const past = new Date(datetime);
    const diffInMinutes = Math.floor((now - past) / (1000 * 60));

    if (diffInMinutes < 0) {
        return `0m 경과`;
    }
    if (diffInMinutes === 0) {
        return `0m 경과`;
    }
    if (diffInMinutes < 60) {
        return `${diffInMinutes}m 경과`;
    } else {
        const hours = Math.floor(diffInMinutes / 60);
        const minutes = diffInMinutes % 60;
        return `${hours}h ${minutes}m 경과`;
    }
};

// 금액을 위한 콤마를 포함한 숫자표현
export const numberWithCommas = x => {
    return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0;
};

// 평균 계산
export const calculateAverage = numbers => {
    if (numbers.length === 0) return 0;
    const sum = numbers.reduce((acc, curr) => acc + curr, 0);
    return Math.round(sum / numbers.length);
};

// 시간 간격 포맷팅 함수 (언어별 표기)
export const formatDurationForDialog = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return '-';

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    const diffInMinutes = Math.floor((end - start) / (1000 * 60));

    if (diffInMinutes < 0) return '-';

    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    return hours > 0 ? i18n.t('time_hours_minutes', { hours, minutes }) : i18n.t('time_minutes_only', { minutes });
};

// 오늘 날짜의 시작 시간 계산 (00:00:00)
export const getTodayStartTime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString();
};

// 오늘 날짜의 끝 시간 계산 (23:59:59.999)
export const getTodayEndTime = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today.toISOString();
};

// 날짜 범위 계산 함수
export const getDateRange = (viewType, selectedDate) => {
    // selectedDate가 유효한지 확인
    if (!selectedDate || !(selectedDate instanceof Date) || isNaN(selectedDate.getTime())) {
        console.error('Invalid selectedDate:', selectedDate);
        // 기본값으로 오늘 날짜 사용
        selectedDate = new Date();
    }

    const startDate = new Date(selectedDate);
    const endDate = new Date(selectedDate);

    switch (viewType) {
        case 'daily': // 일간
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
        case 'weekly': // 주간
            const dayOfWeek = startDate.getDay();
            startDate.setDate(startDate.getDate() - dayOfWeek);
            startDate.setHours(0, 0, 0, 0);
            endDate.setDate(startDate.getDate() + 6);
            endDate.setHours(23, 59, 59, 999);
            break;
        case 'monthly': // 월간
            startDate.setDate(1);
            startDate.setHours(0, 0, 0, 0);
            endDate.setMonth(endDate.getMonth() + 1, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
        case 'yearly': // 연간
            startDate.setMonth(0, 1);
            startDate.setHours(0, 0, 0, 0);
            endDate.setMonth(11, 31);
            endDate.setHours(23, 59, 59, 999);
            break;
        default:
            console.error('Invalid viewType:', viewType);
            // 기본값으로 일간 설정
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
    }

    return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
    };
};

export const getTimeElapsedStr = (val) => {
    if (val === 0) return '경과';
    return `${val}경과`;
};

export const getGenerationStr = (val) => {
    if (val < 10) return '10대 이하';
    return `${val}대`;
};

// 생년월일 입력 필드 표시값 (8자리 완성 시 로케일 형식, 미완성 시 숫자만)
export const getBirthDisplayValue = (birthValue) => {
    const yyyymmdd = String(birthValue ?? '').replace(/[^\d]/g, '');
    if (yyyymmdd.length !== 8) return yyyymmdd;
    return formatDateTimeDisplay(
        `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`,
        'date'
    );
};

// 생년월일 조회 표시 (yyyymmdd -> 나라별 날짜 형식)
export const formatBirthDate = (birthDate) => {
    if (!birthDate || typeof birthDate !== 'string') return birthDate;
    const digits = String(birthDate).replace(/[^\d]/g, '');
    if (digits.length !== 8) return birthDate;
    const yyyy = digits.slice(0, 4);
    const mm = digits.slice(4, 6);
    const dd = digits.slice(6, 8);
    return formatDateTimeDisplay(`${yyyy}-${mm}-${dd}`, 'date');
};

/**
 * lang에 대응하는 `{code}{suffix}` 필드 후보 목록 (zh-Hans 등 하이픈 포함 코드 우선).
 */
const localizedNameFieldKeys = (lang, suffix = '_name') => {
    const code = typeof lang === 'string' && lang.trim() ? lang.trim() : 'en';
    const keys = [`${code}${suffix}`];
    const base = code.split('-')[0];
    if (base && base !== code) {
        keys.push(`${base}${suffix}`);
    }
    return keys;
};

/**
 * 객체에서 언어별 이름 필드(ko_name, en_name, zh-Hans_name, zh-Hant_name 등)를 반환.
 * @param {Object} obj - ko_name, en_name 등 속성을 가진 객체
 * @param {string} lang - 언어 코드 (예: 'ko', 'en', 'zh-Hans', 'zh-Hant')
 * @param {string} [suffix='_name'] - 필드 접미사 (기본 '_name')
 * @returns {string}
 */
export const getLocalizedName = (obj, lang, suffix = '_name') => {
    if (!obj || typeof obj !== 'object') return '';

    for (const field of localizedNameFieldKeys(lang, suffix)) {
        const value = obj[field];
        if (value != null && String(value).trim() !== '') {
            return String(value);
        }
    }

    const fallbacks = [
        obj.en_name,
        obj.ko_name,
        obj.name,
    ];
    for (const value of fallbacks) {
        if (value != null && String(value).trim() !== '') {
            return String(value);
        }
    }
    return '';
};

// 백분율 계산 헬퍼 함수들
export const calculatePercentage = (current, target) => {
    return Math.min(Math.round((current / target) * 100), 100);
};

/**
 * @param {number} minutes - duration in minutes
 * @param {(key: string, opts?: { [k: string]: number }) => string} [t] - i18n translate function (e.g. from useTranslation). If provided, returns translated string.
 */
export const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? i18n.t('time_hours_minutes', { hours, minutes: mins }) : i18n.t('time_minutes_only', { minutes: mins });
};

export const formatCalories = calories => {
    return `${calories}kcal`;
};

// 파싱 함수들
export const parseMinuteSecond = target => {
    const minutes = Math.floor(target / 60);
    const seconds = target % 60;
    return { minutes, seconds };
};

// 웹뷰 환경 감지
export const isWebView = () => {
    return typeof window !== 'undefined' && window.ReactNativeWebView;
};

// 테이블 정렬 관련 유틸리티 함수들
export const createSortConfig = (key = null, direction = 'asc') => ({
    key,
    direction
});

export const handleSort = (currentSortConfig, newKey) => {
    let direction = 'asc';
    if (currentSortConfig.key === newKey && currentSortConfig.direction === 'asc') {
        direction = 'desc';
    }
    return createSortConfig(newKey, direction);
};

export const sortData = (data, sortConfig, numericKeys = [], dateKeys = []) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // 숫자 정렬
        if (numericKeys.includes(sortConfig.key)) {
            aValue = Number(aValue) || 0;
            bValue = Number(bValue) || 0;
        }

        // 날짜 정렬
        if (dateKeys.includes(sortConfig.key)) {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
        }

        if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });
};

// 정렬 아이콘 컴포넌트를 위한 유틸리티
export const getSortIconProps = (columnKey, sortConfig) => {
    const isActive = sortConfig.key === columnKey;
    const isAsc = sortConfig.direction === 'asc';

    return {
        isActive,
        isAsc,
        opacity: isActive ? 1 : 0.3
    };
};

// value로 label을 찾는 함수
export const getLabelByValue = (object, value) => {
    if (value === null || value === undefined) return '';
    const item = object.find(item => item.value === value);
    return item ? item.label : '';
};

/** Coerce any value to a string safe for React children (avoids "Objects are not valid as a React child" for {value, label}). */
export const toDisplayString = (v, fallback = '') => {
    if (v == null) return fallback;
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && v !== null && 'label' in v) return v.label ?? fallback;
    if (typeof v === 'object' && v !== null && 'value' in v) return v.value ?? fallback;
    return String(v);
};

/** 각 단어의 첫 글자를 대문자로. 한글/일본어는 대소문자 없어 그대로 유지됨. */
export const capitalizeEachWord = (str) => {
    if (str == null || typeof str !== 'string') return str;
    return str.split(/\s+/).map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : word).join(' ');
};

// 심박수 구간 계산 함수
export const calculateHeartRateZones = (age, rate) => {
    const maxHeartRate = CHALLENGE_HEARTBEAT_MAX - age;
    return Math.floor(maxHeartRate * rate / 100)
};

// 사용자 역할 판단 함수들
/**
 * grade로 사용자 역할을 반환합니다.
 * @param {number} grade - 사용자 등급
 * @returns {string} 'supervisor' | 'admin' | 'user'
 */
export const getUserRole = (grade) => {
    if (!grade && grade !== 0) return 'user';

    const gradeNum = Number(grade);

    if (gradeNum >= 80) {
        return 'supervisor';
    } else if (gradeNum >= 50 && gradeNum <= 60) {
        return 'admin';
    } else {
        return 'user';
    }
};

/**
 * 슈퍼바이저 여부를 확인합니다.
 * @param {number} grade - 사용자 등급
 * @returns {boolean} 슈퍼어드민이면 true
 */
export const isSupervisor = (grade) => {
    if (!grade && grade !== 0) return false;
    return getUserRole(grade) === 'supervisor';
};

/**
 * 어드민 여부를 확인합니다.
 * @param {number} grade - 사용자 등급
 * @returns {boolean} 어드민이면 true
 */
export const isAdmin = (grade) => {
    if (!grade && grade !== 0) return false;
    return getUserRole(grade) === 'admin';
};

/**
 * 일반 사용자 여부를 확인합니다.
 * @param {number} grade - 사용자 등급
 * @returns {boolean} 일반 사용자이면 true
 */
export const isUser = (grade) => {
    return getUserRole(grade) === 'user';
};